import Phaser from 'phaser';
import { PlayerCard } from '../components/PlayerCard.js';
import { BankrollPill } from '../components/BankrollPill.js';
import { Shimmer } from '../components/Shimmer.js';
import { lobbyLayoutPortrait, type LobbyTableTile, type LobbyLayout, type LobbyRoom } from '../layout/lobby.js';
import { computePlayerCardRect } from '../layout/playerCardRect.js';
import { load, save, getPlayerTier, markUnlockSeenInLobby } from '../persistence.js';
import { loadPrefs, savePrefs } from '../prefs.js';
import { shakeEnabled, hapticsEnabled } from '../feel.js';
import { track } from '../analytics.js';

const OVERLAY_DEPTH = 900;
const OVERLAY_CARD_DEPTH = 901;

// Design tokens — mirror packages/admin/src/html.ts so the floor speaks the
// same visual language as the admin login (gold-on-felt, with the same
// burgundy accents for Salon Privé).
export const FELT         = 0x0d2c1d;
export const FELT_DARK    = 0x08221a;
export const FELT_DEEPER  = 0x051711;
export const GOLD         = 0xd4af37;
export const GOLD_LIGHT   = 0xf4d35e;
export const MUTED        = 0x8a99a3;
export const BODY         = 0xaebfc7;
export const BURGUNDY     = 0x6b1f24;
export const BURGUNDY_TOP = 0x3d1419;
export const BURGUNDY_MID = 0x2a0d10;


/**
 * `?unlock=true` debug — when the URL has the flag, treat every tier as
 * unlocked in the lobby so QA / dev sessions can hit High Limit and
 * Salon Privé without first earning their bankroll gates. Persistence
 * isn't touched, so removing the param reverts to normal lock state on
 * the next reload.
 */
const FORCE_UNLOCK = typeof location !== 'undefined'
  && new URLSearchParams(location.search).get('unlock') === 'true';

interface InfoIconHit {
  tileId: string;
  /** Center in content-space (relative to the scrolling root container). */
  x: number;
  y: number;
  /** Hit-test radius — set generously larger than the visible icon for
   *  finger-friendly tap targets. */
  r: number;
}

type HoverTarget =
  | { kind: 'tile'; tileId: string }
  | { kind: 'info'; tileId: string };

export class LobbyScene extends Phaser.Scene {
  private layout!: LobbyLayout;
  private bankrollPill!: BankrollPill;
  private bankrollCents = 0;
  private overlayCard: PlayerCard | undefined = undefined;
  private overlayBackdrop: Phaser.GameObjects.GameObject[] | undefined = undefined;
  private contentY = 0;
  private dragStartY = 0;
  private dragging = false;
  private roomsAwaitingCelebration = new Set<string>();
  /** Tap targets for per-tile info icons. Populated during drawTile. */
  private infoIconHits: InfoIconHit[] = [];
  /** Live nodes for the currently-open info modal; destroyed on close. */
  private infoModalNodes: Phaser.GameObjects.GameObject[] | undefined;
  /**
   * True while we're consuming a gesture that opened, closed, or
   * dismissed an overlay/modal (player card, info modal, or a pill tap).
   * The matching pointermove + pointerup short-circuit so the gesture
   * never reaches handleTap, which would otherwise select whatever tile
   * sits beneath the dismissed UI. Reset on pointerup so the next
   * gesture starts clean.
   */
  private suppressGesture = false;
  /** Inertial-scroll velocity in px/ms, sampled at pointerup and decayed each frame. */
  private scrollVelocity = 0;
  private lastMoveY = 0;
  private lastMoveTime = 0;
  private contentRoot?: Phaser.GameObjects.Container;
  /** True when the device has a real mouse (matchMedia hover/pointer query).
   *  All hover behavior is dormant when false — touch devices skip it. */
  private hoverCapable = false;
  private hoverTarget: HoverTarget | null = null;
  private hoverOverlay?: Phaser.GameObjects.Graphics;
  /**
   * Per-tile container indexed by tile.id, used by the tap-wiggle and
   * hover-lift animations. Each container is positioned at the tile's
   * visual center; its children are placed in container-local coords
   * (offsets from that center) so rotating / scaling the container
   * pivots around the tile center.
   */
  private tileContainers = new Map<string, Phaser.GameObjects.Container>();
  /** Live shimmer instances created during create(); destroyed on
   *  scene shutdown so masks + tweens don't leak across navigations. */
  private shimmers: Shimmer[] = [];
  /** Wordmark shimmer pieces (bright Text + sweep Graphics) live
   *  OUTSIDE the scrolling contentRoot so the GeometryMask stays
   *  perfectly aligned with the bright copy. We sync their Y each
   *  frame to follow contentY, see `syncWordmarkShimmer`. */
  private wordmarkShimmer?: {
    bright: Phaser.GameObjects.Text;
    stripe: Phaser.GameObjects.Graphics;
    brightRestY: number;
    stripeRestY: number;
  };

  constructor() { super('LobbyScene'); }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;
    const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    this.dpr = dpr;
    const px = (n: number) => Math.round(n * dpr);
    // Lobby card's top/bottom inset — matches the side margin so the gold
    // frame sits symmetrically in the viewport. All content (card frame,
    // brand block, tiles, dividers) is translated down by vMargin via
    // root.y, and the scroll range grows by 2 * vMargin to absorb both gaps.
    const vMargin = px(24);
    this.vMargin = vMargin;
    // Pull live rules from persistence so the floor's stake-line copy reads
    // what the table actually accepts (prevents drift between e.g. tableMin
    // and the "$5–$1,000" label).
    const persisted = load();
    const crapsRules = persisted.games.craps?.tables.standard?.rules as
      | { tableMin: number; tableMax: number; oddsCap: '1x'|'2x'|'3-4-5x'|'5x'|'10x'|'100x' }
      | undefined;
    this.layout = lobbyLayoutPortrait(W, dpr, {
      ...(crapsRules ? { crapsRules } : {}),
    });

    // The layout file hardcodes `unlocked: false` on HL/Whale tiles — sync
    // each tile's flag from persistence so the floor reflects current
    // unlock state. While we're walking tiles, also collect the rooms whose
    // velvet-rope-drop animation hasn't been seen yet so drawRoom can render
    // them in the initial-locked aesthetic and the animation can flip them
    // to live afterward.
    const persistedAtCreate = persisted;
    const roomsAwaitingCelebration = new Set<string>();
    for (const room of this.layout.rooms) {
      for (const tile of room.tiles) {
        const [gameId, tableKey] = tile.id.split(':') as [string, string | undefined];
        if (!tableKey) continue;
        const t = persistedAtCreate.games[gameId as 'craps']
          ?.tables[tableKey];
        if (!t) continue;
        tile.unlocked = t.unlocked || FORCE_UNLOCK;
        if (t.unlocked && !t.unlockSeenInLobby) {
          roomsAwaitingCelebration.add(room.id);
        }
      }
    }
    this.roomsAwaitingCelebration = roomsAwaitingCelebration;

    // Background layers, viewport-fixed (NOT in contentRoot so they don't
    // scroll). Order: solid felt-dark → diagonal stripe overlay → radial
    // gold-glow accent at the top. All sit at negative depth so the scrolling
    // room content always draws on top. The legacy per-room felt bands still
    // fill the viewport edge-to-edge in this commit, so the bg is only
    // visible in places the rooms don't cover (which is nowhere today — the
    // card frame in the next commit insets the room area and reveals it).
    this.add
      .rectangle(0, 0, W, H, FELT_DARK)
      .setOrigin(0, 0)
      .setDepth(-100);
    const stripeSize = Math.ceil(Math.max(W, H) * 1.5);
    this.add
      .tileSprite(W / 2, H / 2, stripeSize, stripeSize, 'lobby-stripe')
      .setOrigin(0.5, 0.5)
      .setRotation(-Math.PI / 4)
      .setTileScale(dpr, dpr)
      .setDepth(-99);
    const glowSize = Math.max(W, H) * 1.2;
    this.add
      .image(W / 2, Math.round(H * 0.12), 'lobby-glow')
      .setDisplaySize(glowSize, glowSize)
      .setDepth(-98);

    const root = this.add.container(0, 0);
    root.setDepth(0);

    // Reset per-scene state — create() runs again on every navigation back
    // to the lobby, so stale icon hit boxes, tile containers, and shimmer
    // instances from a previous mount must not bleed into the new layout.
    this.infoIconHits = [];
    this.tileContainers.clear();
    this.shimmers = [];

    // contentY is an instance field, so it survives scene.stop()/start();
    // the root container is rebuilt fresh at y=0 each create(). Without this
    // re-application, the floor renders at the top, and the first finger
    // movement snaps it back to the saved offset because pointerdown's
    // dragStartY was computed from stale contentY. Clamp to current bounds
    // since an unlock between visits can change scrollHeight. The clamp
    // subtracts 2*vMargin because we reserve that space for the top and
    // bottom card-frame insets.
    this.contentY = Phaser.Math.Clamp(
      this.contentY,
      Math.min(0, H - this.layout.scrollHeight - 2 * vMargin),
      0,
    );
    root.setY(this.contentY + vMargin);
    this.scrollVelocity = 0;
    this.dragging = false;

    // Card frame fill — admin-login "lobby-card" framing applied to the
    // whole floor. Drawn first inside contentRoot so subsequent room and
    // tile geometry stacks on top. The card extends the full scrollable
    // height; sides are inset to match the tile letterbox (≤480 buffer-px
    // wide). The legacy per-room felt bands still cover the card fill in
    // this commit; the gold stroke that's drawn at the end of create()
    // remains visible on top of them.
    const cardW = Math.min(W - px(48), px(480));
    const cardX = Math.round((W - cardW) / 2);
    const cardH = this.layout.scrollHeight;
    const cardR = px(16);
    const cardFill = this.add.graphics();
    cardFill.fillStyle(FELT, 1);
    cardFill.fillRoundedRect(cardX, 0, cardW, cardH, cardR);
    root.add(cardFill);

    // Brand block — suits row, CASINO wordmark, FLOOR subtitle. Top of card,
    // centered. Mirrors packages/admin/src/html.ts's login header so the
    // floor opens with the same visual statement as the admin entry point.
    const suitsY    = px(32);
    const casinoY   = suitsY + px(14) + px(18);
    const floorY    = casinoY + px(38) + px(6);
    const fontFamily = '"Helvetica Neue", Helvetica, Arial, sans-serif';
    const suits = this.add.text(W / 2, suitsY, '♠ ♥ ♦ ♣', {
      fontFamily,
      fontSize: `${px(14)}px`,
      color: '#d4af37',
    }).setOrigin(0.5, 0).setAlpha(0.85);
    suits.setLetterSpacing(px(14));
    root.add(suits);

    const brand = this.add.text(W / 2, casinoY, 'CASINO', {
      fontFamily,
      fontSize: `${px(38)}px`,
      color: '#f4d35e',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    brand.setLetterSpacing(px(6));
    root.add(brand);

    // Wordmark shimmer — a near-white duplicate of CASINO sits on top of
    // the resting gold copy with BLEND_MODES.ADD, masked by a moving
    // Graphics strip so only a narrow vertical band shows at any time.
    // The bright copy + mask live OUTSIDE the scrolling contentRoot
    // because Phaser's GeometryMask doesn't reliably apply a parent
    // container's transform — putting them in the container drifts the
    // mask off the text as soon as the user scrolls. Instead they're
    // top-level scene objects whose Y is synced to root.y every frame
    // (see syncWordmarkShimmer), which keeps the mask perfectly aligned
    // with the brightBrand at all scroll positions.
    const brandLeftX = brand.x - brand.width / 2;
    const brandRightX = brand.x + brand.width / 2;
    const stripeW = Math.round(brand.width * 0.18);
    const stripeH = Math.round(brand.height * 1.4);
    const stripeRestY = brand.y - Math.round(brand.height * 0.2);
    const sweepStartX = brandLeftX - stripeW;
    const sweepEndX = brandRightX;

    const brightBrand = this.add.text(W / 2, casinoY, 'CASINO', {
      fontFamily,
      fontSize: `${px(38)}px`,
      color: '#fff7c2',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    brightBrand.setLetterSpacing(px(6));
    brightBrand.setBlendMode(Phaser.BlendModes.ADD);
    // Depth above the lobby content (contentRoot is depth 0) but well
    // below the overlay layers (OVERLAY_DEPTH = 900).
    brightBrand.setDepth(50);

    const stripeGraphics = this.add.graphics();
    stripeGraphics.fillStyle(0xffffff);
    stripeGraphics.fillRect(0, 0, stripeW, stripeH);
    stripeGraphics.setPosition(sweepStartX, stripeRestY);
    stripeGraphics.setVisible(false);
    stripeGraphics.setDepth(50);
    brightBrand.setMask(stripeGraphics.createGeometryMask());

    this.tweens.add({
      targets: stripeGraphics,
      x: sweepEndX,
      duration: 1100,
      ease: 'Sine.InOut',
      repeat: -1,
      repeatDelay: 1500,
      onRepeat: () => stripeGraphics.setX(sweepStartX),
    });

    // brightRestY/stripeRestY bake in vMargin: brand text lives inside the
    // scrolling root (whose y now includes vMargin), but the bright copy +
    // mask are scene-root objects. syncWordmarkShimmer adds contentY only,
    // so the rest values need the vMargin offset to stay aligned.
    this.wordmarkShimmer = {
      bright: brightBrand,
      stripe: stripeGraphics,
      brightRestY: casinoY + vMargin,
      stripeRestY: stripeRestY + vMargin,
    };
    // Apply once now — contentY may already be non-zero on a return
    // navigation, in which case the bright copy must start aligned
    // (otherwise the first frame is visibly off before tickInertia fires).
    this.syncWordmarkShimmer();

    const floor = this.add.text(W / 2, floorY, 'FLOOR', {
      fontFamily,
      fontSize: `${px(11)}px`,
      color: '#8a99a3',
    }).setOrigin(0.5, 0);
    floor.setLetterSpacing(px(4));
    root.add(floor);

    for (const room of this.layout.rooms) {
      this.drawRoom(root, room, W);
    }

    // Card frame stroke — drawn last so the gold border sits on top of the
    // per-room felt bands. (Once the rooms are converted to live inside the
    // card's FELT fill — commit 3 — the per-room fills go away and the
    // border simply traces the card outline cleanly.)
    const cardStroke = this.add.graphics();
    cardStroke.lineStyle(px(1), GOLD, 1);
    cardStroke.strokeRoundedRect(cardX, 0, cardW, cardH, cardR);
    root.add(cardStroke);

    // Hover affordances are desktop-only. The hover/pointer media query is the
    // standard way to ask "is this a real mouse, or a finger pretending?"
    // Touch devices report `hover: none, pointer: coarse` and skip the wiring.
    this.hoverCapable =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.hoverTarget = null;
    if (this.hoverCapable) {
      // Parented to contentRoot so the overlay scrolls in lockstep with the
      // tiles — drawn in content-space coordinates, no manual sync on scroll.
      this.hoverOverlay = this.add.graphics();
      root.add(this.hoverOverlay);
    }

    // Defer to next tick so the rooms are fully drawn before the animation
    // starts. The rope-drop animation flips `unlockSeenInLobby` per-tile when
    // it completes, so subsequent lobby visits don't replay it.
    if (this.roomsAwaitingCelebration.size > 0) {
      this.time.delayedCall(100, () => this.playUnlockAnimations());
    }

    // Bankroll pill (top-right, viewport coords) — tap toggles a full
    // PlayerCard overlay matching the craps treatment.
    this.bankrollCents = persisted.bankrollCents;
    const sideMargin = Math.round(12 * dpr);
    this.bankrollPill = new BankrollPill({
      scene: this,
      rightX: W - sideMargin,
      topY: Math.round(14 * dpr),
      dpr,
      onClick: () => {
        // Pill listener runs BEFORE the scene-level pointerdown, so
        // toggling the overlay here would otherwise leave the matching
        // pointerup falling through to handleTap. Flag the gesture as
        // consumed so move/up bail before any tile gets selected.
        this.suppressGesture = true;
        this.toggleBankrollOverlay();
      },
    });
    this.bankrollPill.setBankrollCents(this.bankrollCents);

    // Touch / scroll. Drag follows the finger; on pointerup, residual
    // velocity sampled during the move kicks off an inertial-decay tick that
    // continues scrolling after release ("flick and throw").
    let downX = 0;
    let downY = 0;
    const tapSlop = Math.round(8 * dpr);
    this.contentRoot = root;
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      // Pill onClick (which runs BEFORE this scene-level listener) may
      // have already flagged the gesture as consumed (toggling the
      // bankroll overlay). Bail without starting a drag so handleTap
      // can't fire on the matching pointerup.
      if (this.suppressGesture) return;
      // Info modal: any tap dismisses, and the entire gesture is then
      // consumed so it can't propagate to handleTap.
      if (this.infoModalNodes) {
        this.closeInfoModal();
        this.suppressGesture = true;
        return;
      }
      // Player card overlay: any tap on the backdrop dismisses (taps on
      // the pill itself were already consumed via the pill's onClick
      // path above). Same gesture-suppression rule applies.
      if (this.overlayCard) {
        this.closeBankrollOverlay();
        this.suppressGesture = true;
        return;
      }
      // A new touch immediately cancels any in-flight inertia — the finger
      // is the source of truth.
      this.scrollVelocity = 0;
      this.dragging = true;
      this.dragStartY = p.y - this.contentY;
      this.lastMoveY = p.y;
      this.lastMoveTime = performance.now();
      downX = p.x; downY = p.y;
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.suppressGesture) return;
      if (!this.dragging) {
        // Mouse is moving without a drag in progress — update hover.
        this.updateHoverFromPointer(p.x, p.y);
        return;
      }
      this.contentY = Phaser.Math.Clamp(
        p.y - this.dragStartY,
        Math.min(0, H - this.layout.scrollHeight - 2 * vMargin),
        0,
      );
      root.setY(this.contentY + vMargin);
      // Sample instantaneous velocity (px/ms) and EMA-smooth so a single
      // jittery frame doesn't overshoot. The smoothed value is what
      // tickInertia consumes after pointerup.
      const now = performance.now();
      const dt = now - this.lastMoveTime;
      if (dt > 0) {
        const inst = (p.y - this.lastMoveY) / dt;
        this.scrollVelocity = this.scrollVelocity * 0.5 + inst * 0.5;
      }
      this.lastMoveY = p.y;
      this.lastMoveTime = now;
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      // Pair to the pointerdown short-circuit above. The flag is
      // consumed here so the next gesture starts clean.
      if (this.suppressGesture) {
        this.suppressGesture = false;
        this.dragging = false;
        return;
      }
      this.dragging = false;
      const dx = Math.abs(p.x - downX);
      const dy = Math.abs(p.y - downY);
      if (dx + dy < tapSlop) {
        // It was a tap, not a flick — kill any sampled velocity so we don't
        // accidentally kick off inertia from a finger jiggle.
        this.scrollVelocity = 0;
        // Subtract vMargin: tile.y is in root-local coords, but root.y
        // now includes the vMargin offset (so p.y - this.contentY would
        // be off by vMargin without this correction).
        this.handleTap(p.x, p.y - this.contentY - vMargin);
        return;
      }
      // Stale velocity (finger held still before release) shouldn't fling.
      if (performance.now() - this.lastMoveTime > 100) this.scrollVelocity = 0;
    });

    // Mouse wheel for desktop — direct, no inertia.
    this.input.on('wheel', (p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      this.scrollVelocity = 0;
      this.contentY = Phaser.Math.Clamp(
        this.contentY - dy,
        Math.min(0, H - this.layout.scrollHeight - 2 * vMargin),
        0,
      );
      root.setY(this.contentY + vMargin);
      // Tile under the cursor changed even though the pointer didn't move —
      // recompute hover from the wheel event's pointer coords.
      this.updateHoverFromPointer(p.x, p.y);
    });

    // Per-frame inertial decay. Decoupled from input events so velocity
    // continues to be applied across many frames after pointerup.
    this.events.on(Phaser.Scenes.Events.UPDATE, this.tickInertia, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.UPDATE, this.tickInertia, this);
      // Reset cursor so it doesn't leak into the next scene (table view).
      if (this.hoverCapable) this.input.setDefaultCursor('');
      // Shimmer masks + repeating tweens aren't owned by the contentRoot
      // container's auto-cleanup — destroy them explicitly so a return
      // navigation doesn't leak phantom masks against destroyed Text.
      for (const s of this.shimmers) s.destroy();
      this.shimmers = [];
      this.tileContainers.clear();
      // The wordmark shimmer lives outside contentRoot; explicitly
      // tear it down so its tween + GeometryMask shape don't outlive
      // the scene.
      if (this.wordmarkShimmer) {
        this.tweens.killTweensOf(this.wordmarkShimmer.stripe);
        this.wordmarkShimmer.bright.clearMask();
        this.wordmarkShimmer.bright.destroy();
        this.wordmarkShimmer.stripe.destroy();
        delete this.wordmarkShimmer;
      }
    });
  }

  /** Translate a viewport-space pointer into a hover target, then commit. */
  private updateHoverFromPointer(px: number, py: number): void {
    if (!this.hoverCapable) return;
    // Bail (and clear hover) when an interaction owns the gesture or
    // something is occluding the floor.
    if (
      this.dragging ||
      this.suppressGesture ||
      this.infoModalNodes ||
      this.overlayCard
    ) {
      this.setHover(null);
      return;
    }
    // Subtract vMargin: tile.y is in root-local coords, but root.y now
    // includes the vMargin offset for the top card-frame inset.
    const contentY = py - this.contentY - this.vMargin;
    // Info icon takes priority (matches handleTap's resolution order).
    for (const hit of this.infoIconHits) {
      const dx = px - hit.x;
      const dy = contentY - hit.y;
      if (dx * dx + dy * dy <= hit.r * hit.r) {
        this.setHover({ kind: 'info', tileId: hit.tileId });
        return;
      }
    }
    for (const room of this.layout.rooms) {
      for (const tile of room.tiles) {
        if (
          px >= tile.x && px <= tile.x + tile.w &&
          contentY >= tile.y && contentY <= tile.y + tile.h
        ) {
          this.setHover({ kind: 'tile', tileId: tile.id });
          return;
        }
      }
    }
    this.setHover(null);
  }

  private setHover(target: HoverTarget | null): void {
    const same =
      (target === null && this.hoverTarget === null) ||
      (target !== null &&
        this.hoverTarget !== null &&
        target.kind === this.hoverTarget.kind &&
        target.tileId === this.hoverTarget.tileId);
    if (same) return;
    // Hover-lift: which tile (if any) was lifted and which one should be
    // now. Only tile-body hovers lift; info-icon hovers leave the tile
    // alone. Tween targets the per-tile container's scale so the lift
    // pivots around the tile center.
    const prevLiftId = this.hoverTarget?.kind === 'tile' ? this.hoverTarget.tileId : null;
    const nextLiftId = target?.kind === 'tile' ? target.tileId : null;
    if (prevLiftId !== nextLiftId) {
      if (prevLiftId) this.tweenTileScale(prevLiftId, 1);
      if (nextLiftId) this.tweenTileScale(nextLiftId, 1.02);
    }
    this.hoverTarget = target;
    this.input.setDefaultCursor(target ? 'pointer' : '');
    this.redrawHoverOverlay();
  }

  private tweenTileScale(tileId: string, scale: number): void {
    const container = this.tileContainers.get(tileId);
    if (!container) return;
    this.tweens.add({
      targets: container,
      scale,
      duration: 140,
      ease: 'Sine.Out',
    });
  }

  private redrawHoverOverlay(): void {
    const g = this.hoverOverlay;
    if (!g) return;
    g.clear();
    const target = this.hoverTarget;
    if (!target) return;
    const tile = this.findTile(target.tileId);
    if (!tile) return;
    const px = (n: number) => Math.round(n * this.dpr);

    if (target.kind === 'info') {
      // Find the icon center via the cached hit (drawInfoIcon recorded it
      // with the visible-icon coords + an inflated radius for taps).
      const hit = this.infoIconHits.find((h) => h.tileId === tile.id);
      if (!hit) return;
      const iconR = px(13);
      g.fillStyle(0xf4d35e, 1);
      g.fillCircle(hit.x, hit.y, iconR);
      g.lineStyle(px(1.5), 0x1a0e08, 0.55);
      g.strokeCircle(hit.x, hit.y, iconR);
      // Halo just outside the icon for a "you're on this" cue.
      g.lineStyle(px(2), 0xf4d35e, 0.55);
      g.strokeCircle(hit.x, hit.y, iconR + px(3));
      return;
    }

    // Tile-body hover: no overlay. The hover-lift scale (handled in
    // setHover) plus the pointer cursor are enough affordance — the
    // previous brighter-stroke overlay sat on top of the resting gold
    // border and read as a doubled outline.
  }

  /**
   * Sync the wordmark shimmer (bright Text + sweep Graphics) to the
   * current scroll position. They live OUTSIDE the contentRoot because
   * Phaser's GeometryMask doesn't propagate a parent container's
   * transform to the mask shape, so as soon as contentY changes the
   * mask drifts off the bright text. Instead we keep them at the
   * scene level and translate them in lockstep with contentY here, so
   * both objects share identical world coords with the underlying
   * (scrolling) gold wordmark.
   */
  private syncWordmarkShimmer(): void {
    if (!this.wordmarkShimmer) return;
    const s = this.wordmarkShimmer;
    s.bright.setY(s.brightRestY + this.contentY);
    s.stripe.setY(s.stripeRestY + this.contentY);
  }

  private tickInertia(_time: number, deltaMs: number): void {
    // Keep the wordmark shimmer aligned every frame so it tracks both
    // drag-driven scrolls (which don't trigger inertia) and inertial
    // decay. Cheap — two setY calls when state hasn't changed.
    this.syncWordmarkShimmer();
    if (this.dragging || !this.contentRoot) return;
    if (Math.abs(this.scrollVelocity) < 0.05) {
      this.scrollVelocity = 0;
      return;
    }
    const H = this.scale.height;
    const minY = Math.min(0, H - this.layout.scrollHeight - 2 * this.vMargin);
    const proposed = this.contentY + this.scrollVelocity * deltaMs;
    const clamped = Phaser.Math.Clamp(proposed, minY, 0);
    this.contentY = clamped;
    this.contentRoot.setY(clamped + this.vMargin);
    if (this.hoverCapable) {
      const p = this.input.activePointer;
      this.updateHoverFromPointer(p.x, p.y);
    }
    if (clamped !== proposed) {
      // Hit a bound — stop dead. (No bounce; matches the existing drag
      // behavior at the edges.)
      this.scrollVelocity = 0;
      return;
    }
    // Exponential decay, normalized to 60 fps. ~0.94 per 16ms gives a
    // half-life around 180ms — quick enough to feel responsive on a short
    // flick, slow enough to coast through the whole lobby on a hard one.
    this.scrollVelocity *= Math.pow(0.94, deltaMs / 16);
  }

  private dpr = 1;
  private vMargin = 0;

  private toggleBankrollOverlay() {
    if (this.overlayCard) this.closeBankrollOverlay();
    else this.openBankrollOverlay();
  }

  private openBankrollOverlay() {
    const W = this.scale.width;
    const H = this.scale.height;
    // Backdrop is a non-interactive scrim — the scene-level pointerdown
    // handler owns dismiss-on-tap-outside, which also gates the gesture
    // so the tap can't propagate to handleTap and select a tile.
    const dim = this.add.graphics().setDepth(OVERLAY_DEPTH);
    dim.fillStyle(0x000000, 0.55);
    dim.fillRect(0, 0, W, H);
    this.overlayBackdrop = [dim];

    const hasVibrate = typeof navigator !== 'undefined' && 'vibrate' in navigator;
    const toggles: Array<{ label: string; initial: boolean; onChange: (v: boolean) => void }> = [
      {
        label: 'Screen shake',
        initial: shakeEnabled(),
        onChange: (v) => savePrefs({ ...loadPrefs(), screenShake: v ? 'on' : 'off' }),
      },
    ];
    if (hasVibrate) {
      toggles.push({
        label: 'Vibration',
        initial: hapticsEnabled(),
        onChange: (v) => savePrefs({ ...loadPrefs(), haptics: v ? 'on' : 'off' }),
      });
    }
    this.overlayCard = new PlayerCard({
      scene: this,
      rect: computePlayerCardRect(W, H),
      dpr: this.dpr,
      brandText: 'Casino',
      primaryButton: null,
      secondaryButton: {
        label: 'RESET',
        onClick: () => {
          import('../persistence.js').then(({ reset }) => {
            reset();
            location.reload();
          });
        },
      },
      toggles,
      tier: getPlayerTier(load()),
    });
    this.overlayCard.setDepth(OVERLAY_CARD_DEPTH);
    this.overlayCard.setBankrollCents(this.bankrollCents);
    this.bankrollPill.setDepth(OVERLAY_CARD_DEPTH);
    this.bankrollPill.setExpanded(true);
    this.setHover(null);
  }

  private closeBankrollOverlay() {
    if (this.overlayBackdrop) {
      for (const o of this.overlayBackdrop) o.destroy();
      this.overlayBackdrop = undefined;
    }
    this.overlayCard?.destroy();
    this.overlayCard = undefined;
    this.bankrollPill.setDepth(0);
    this.bankrollPill.setExpanded(false);
    if (this.hoverCapable) {
      const p = this.input.activePointer;
      this.updateHoverFromPointer(p.x, p.y);
    }
  }

  private drawRoom(root: Phaser.GameObjects.Container, room: LobbyRoom, W: number) {
    // Rangebar pill divider — admin-login's room-rule applied to the floor.
    // Two horizontal gold-gradient hairlines flank a centered pill at
    // room.dividerY; the pill's color depends on the room (entrance gold,
    // pit burgundy, salon-prive gold-light-on-burgundy).
    if (room.velvetRope) this.drawRangebarDivider(root, room, W);
    for (const tile of room.tiles) this.drawTile(root, tile, room.id);
  }

  private drawRangebarDivider(
    root: Phaser.GameObjects.Container,
    room: LobbyRoom,
    W: number,
  ): void {
    const px = (n: number) => Math.round(n * this.dpr);
    const cy = room.dividerY;
    const labelText = `◆ ${room.label} ◆`;

    // Pick pill colors. Salon Privé gets a darker burgundy fill with a
    // gold-light stroke; locked non-salon rooms (i.e., the pit while
    // anything inside it is still locked) get burgundy@30 with a softer
    // stroke; everything else gets the resting gold-on-felt treatment.
    const isSalon = room.id === 'salon-prive';
    const anyUnlocked = room.tiles.some((t) => t.unlocked);
    const isLockedRoom = !anyUnlocked && room.id !== 'entrance';
    let pillFill: number, pillFillAlpha: number;
    let pillStroke: number, pillStrokeAlpha: number;
    if (isSalon) {
      pillFill = BURGUNDY_TOP; pillFillAlpha = 0.55;
      pillStroke = GOLD_LIGHT; pillStrokeAlpha = 1;
    } else if (isLockedRoom) {
      pillFill = BURGUNDY;    pillFillAlpha = 0.30;
      pillStroke = GOLD;      pillStrokeAlpha = 0.55;
    } else {
      pillFill = GOLD;        pillFillAlpha = 0.08;
      pillStroke = GOLD;      pillStrokeAlpha = 1;
    }

    // Measure the label once so the pill can size to its content.
    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${px(11)}px`,
      color: '#f4d35e',
      fontStyle: 'bold',
    };
    const label = this.add.text(0, 0, labelText, labelStyle).setOrigin(0.5);
    label.setLetterSpacing(px(3));

    const padX = px(14);
    const padY = px(5);
    const pillW = label.width + padX * 2;
    const pillH = label.height + padY * 2;
    const pillX = Math.round((W - pillW) / 2);
    const pillY = Math.round(cy - pillH / 2);

    // Hairlines on either side of the pill, inset to the card's interior.
    const cardW = Math.min(W - px(48), px(480));
    const cardX = Math.round((W - cardW) / 2);
    const innerPad = px(24);
    const lineGap = px(12);
    const leftStartX = cardX + innerPad;
    const leftEndX = pillX - lineGap;
    const rightStartX = pillX + pillW + lineGap;
    const rightEndX = cardX + cardW - innerPad;
    if (leftEndX > leftStartX) {
      this.addHairline(root, leftStartX, cy, leftEndX - leftStartX);
    }
    if (rightEndX > rightStartX) {
      this.addHairline(root, rightStartX, cy, rightEndX - rightStartX);
    }

    // Pill bg + border, then label on top.
    const pillBg = this.add.graphics();
    pillBg.fillStyle(pillFill, pillFillAlpha);
    pillBg.fillRoundedRect(pillX, pillY, pillW, pillH, pillH / 2);
    pillBg.lineStyle(px(1), pillStroke, pillStrokeAlpha);
    pillBg.strokeRoundedRect(pillX, pillY, pillW, pillH, pillH / 2);
    root.add(pillBg);

    label.setPosition(W / 2, cy);
    root.add(label);
  }

  private addHairline(
    root: Phaser.GameObjects.Container,
    x: number,
    cy: number,
    w: number,
  ): void {
    // 256x4 canvas texture rendered as a stretched Image. The texture is
    // pre-painted with a transparent → gold@65% → transparent gradient so
    // the line fades naturally toward the pill and the card edge.
    const img = this.add.image(x, cy, 'lobby-hairline').setOrigin(0, 0.5);
    img.setDisplaySize(w, Math.max(2, Math.round(this.dpr * 1.5)));
    root.add(img);
  }

  private drawTile(
    root: Phaser.GameObjects.Container,
    tile: LobbyTableTile,
    roomId: LobbyRoom['id'],
  ) {
    const px = (n: number) => Math.round(n * this.dpr);
    const isTutorial = tile.id === 'craps:tutorial';
    const isSalonLocked = !tile.unlocked && roomId === 'salon-prive';
    const isLocked = !tile.unlocked;

    // Each tile is wrapped in its own Container positioned at the tile's
    // visual center. Children are laid out in container-local coords
    // (offsets from that center), so rotating the container during a
    // tap-wiggle pivots around the tile center; scaling for hover-lift
    // grows the tile symmetrically. The hit-test bounds (still tile.x /
    // tile.y absolute) stay valid because the container's resting state
    // is angle=0, scale=1.
    const tileCx = tile.x + tile.w / 2;
    const tileCy = tile.y + tile.h / 2;
    const tileContainer = this.add.container(tileCx, tileCy);
    root.add(tileContainer);
    this.tileContainers.set(tile.id, tileContainer);

    // Tile body — uniform rounded-rect (no more shape-based silhouettes;
    // the room context conveys what kind of game lives here). Fill gradient
    // and stroke alpha vary by lock state to match admin-login's .card
    // treatment for resting / locked / salon-locked variants.
    let fillTop: number, fillBot: number;
    let strokeAlpha: number;
    if (isSalonLocked) {
      fillTop = 0x2a0d10; fillBot = 0x180609; strokeAlpha = 0.45;
    } else if (isLocked) {
      fillTop = 0x14232f; fillBot = 0x0c1c16; strokeAlpha = 0.4;
    } else {
      fillTop = 0x0d4a32; fillBot = 0x08402d; strokeAlpha = 1;
    }
    const tileR = px(14);
    const halfW = tile.w / 2;
    const halfH = tile.h / 2;

    const g = this.add.graphics();
    g.fillGradientStyle(fillTop, fillTop, fillBot, fillBot, 1);
    g.fillRoundedRect(-halfW, -halfH, tile.w, tile.h, tileR);
    g.lineStyle(px(1), GOLD, strokeAlpha);
    g.strokeRoundedRect(-halfW, -halfH, tile.w, tile.h, tileR);
    tileContainer.add(g);

    // Layout: label (with optional info icon) + stake stacked on the left;
    // Enter button on the right (or LOCKED badge top-right for locked).
    const padX = px(18);
    const labelStr = tile.label.toUpperCase();
    const labelFontPx = isTutorial ? px(15) : px(18);
    const labelTracking = isTutorial ? px(2) : px(3);
    const stakeFontPx = px(11);
    const lockReasonFontPx = px(11);
    const gapAfterLabel = px(6);
    const gapAfterStake = px(6);

    // Build all text objects first so we can measure their heights and
    // center the column vertically. Positions below are container-local
    // (relative to the tile center).
    const label = this.add.text(0, 0, labelStr, {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${labelFontPx}px`,
      color: isLocked ? '#d4af37' : '#f4d35e',
      fontStyle: 'bold',
    });
    label.setLetterSpacing(labelTracking);
    if (isLocked) label.setAlpha(0.6);

    const stake = this.add.text(0, 0, tile.stakeText.toUpperCase(), {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${stakeFontPx}px`,
      color: '#8a99a3',
    });
    stake.setLetterSpacing(px(1));
    if (isLocked) stake.setAlpha(0.55);

    const showLockReason = isLocked && !!tile.lockReason && !isTutorial;
    let lockReason: Phaser.GameObjects.Text | undefined;
    if (showLockReason) {
      lockReason = this.add.text(0, 0, tile.lockReason as string, {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${lockReasonFontPx}px`,
        color: '#8a99a3',
        fontStyle: 'italic',
      });
    }

    const colH =
      label.height + gapAfterLabel + stake.height +
      (lockReason ? gapAfterStake + lockReason.height : 0);
    const colTopRel = -halfH + Math.round((tile.h - colH) / 2);
    const colLeftRel = -halfW + padX;

    label.setPosition(colLeftRel, colTopRel);
    stake.setPosition(colLeftRel, colTopRel + label.height + gapAfterLabel);
    if (lockReason) {
      lockReason.setPosition(
        colLeftRel,
        colTopRel + label.height + gapAfterLabel + stake.height + gapAfterStake,
      );
    }
    tileContainer.add(label);
    tileContainer.add(stake);
    if (lockReason) tileContainer.add(lockReason);

    // Info icon — right next to the label, vertically centered to it. The
    // tutorial tile omits it (its rules live in the tutorial itself). The
    // hit position is recorded in absolute (contentRoot) coords so the
    // existing handleTap / hover paths don't have to traverse containers.
    if (!isTutorial) {
      const iconR = px(11);
      const iconGap = px(8);
      const iconCxRel = colLeftRel + label.width + iconGap + iconR;
      const iconCyRel = colTopRel + label.height / 2;
      this.drawInfoIcon(
        tileContainer, tile,
        iconCxRel, iconCyRel,
        tileCx + iconCxRel, tileCy + iconCyRel,
      );
    }

    if (!tile.unlocked) {
      // LOCKED badge in the top-right corner. Salon-locked variant flips
      // the badge text to gold-light so it reads against the burgundy.
      // Slow alpha pulse (0.7 ↔ 1.0, 1800ms period) keeps the eye drawn
      // to "this is reachable but not yet."
      const badge = this.add.text(halfW - px(10), -halfH + px(10), 'LOCKED', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${px(9)}px`,
        color: isSalonLocked ? '#f4d35e' : '#d4af37',
        backgroundColor: 'rgba(0,0,0,0.55)',
        padding: { x: px(6), y: px(3) },
        fontStyle: 'bold',
      }).setOrigin(1, 0);
      badge.setLetterSpacing(px(2));
      tileContainer.add(badge);
      this.tweens.add({
        targets: badge,
        alpha: { from: 1, to: 0.7 },
        duration: 900,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
      });
    } else {
      // Enter button — gold pill with felt text, vertically centered on
      // the right side. Visual only; the tap target is the whole tile via
      // handleTap so the button needs no input wiring.
      const btnText = 'ENTER →';
      const btnFontPx = px(11);
      const btnPadX = px(14);
      const btnPadY = px(8);
      const btn = this.add.text(0, 0, btnText, {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${btnFontPx}px`,
        color: '#0d2c1d',
        fontStyle: 'bold',
      });
      btn.setLetterSpacing(px(2));
      const btnW = btn.width + btnPadX * 2;
      const btnH = btn.height + btnPadY * 2;
      const btnXRel = halfW - padX - btnW;
      const btnYRel = -btnH / 2;
      const btnBg = this.add.graphics();
      btnBg.fillStyle(GOLD, 1);
      btnBg.fillRoundedRect(btnXRel, btnYRel, btnW, btnH, px(8));
      btn.setPosition(btnXRel + btnPadX, btnYRel + btnPadY);
      tileContainer.add(btnBg);
      tileContainer.add(btn);

      // "Tap me" bounce — mirrors the craps-scene chip jiggle: two
      // sine waves of different frequencies (per axis) sum into a
      // pseudo-random wander, modulated by a slow envelope so the
      // intensity breathes. A per-tile phase offset keeps adjacent
      // buttons from moving in lockstep. Amplitudes are scaled by
      // dpr so the motion reads consistently across screens.
      const jigglePhase = Math.random() * 100;
      // btnBg is a Graphics whose rect is drawn at LOCAL (btnXRel, btnYRel)
      // inside its own path, so the graphics object's own position is the
      // OFFSET applied to the rendered rect. The Text's position is its
      // absolute position in the tile container, so its rest is offset by
      // padding from the rect's top-left.
      const btnTextRestX = btnXRel + btnPadX;
      const btnTextRestY = btnYRel + btnPadY;
      const jiggleAmp = this.dpr;
      this.tweens.addCounter({
        from: 0,
        to: 1,
        duration: 4000,
        repeat: -1,
        onUpdate: () => {
          const t = this.time.now / 1000 + jigglePhase;
          const envelope = 0.7 + 0.3 * Math.sin(t * 1.3);
          const dx = (Math.sin(t * 14.3) * 0.9 + Math.sin(t * 27.1) * 0.45) * envelope * jiggleAmp;
          const dy = (Math.cos(t * 12.7) * 0.85 + Math.cos(t * 25.4) * 0.42) * envelope * jiggleAmp;
          btnBg.setPosition(dx, dy);
          btn.setPosition(btnTextRestX + dx, btnTextRestY + dy);
        },
      });

      // Shimmer sweep over the unlocked tile — the gold border briefly
      // brightens as the band passes. Staggered start so adjacent tiles
      // don't sweep in lockstep.
      const startDelay = (tile.id.length * 313) % 4000;
      this.shimmers.push(new Shimmer({
        scene: this,
        parent: tileContainer,
        bounds: new Phaser.Geom.Rectangle(-halfW, -halfH, tile.w, tile.h),
        periodMs: 5500,
        peakAlpha: 0.18,
        bandRatio: 0.30,
        startDelayMs: startDelay,
      }));

      // Label shimmer — a swept-strip duplicate of the tile name,
      // sitting on top of the gold copy with BLEND_MODES.ADD so it
      // brightens the letterforms as it passes. The crop window
      // animates across the bright copy in the text's *local* frame,
      // which means parent-container transforms (scroll, hover-lift
      // scale, tap wiggle) can't desync it from the underlying gold
      // label — the gleam always tracks the glyphs exactly.
      const brightLabel = this.add.text(0, 0, labelStr, {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${labelFontPx}px`,
        color: '#ffffff',
        fontStyle: 'bold',
      });
      brightLabel.setLetterSpacing(labelTracking);
      brightLabel.setBlendMode(Phaser.BlendModes.ADD);
      brightLabel.setPosition(colLeftRel, colTopRel);
      brightLabel.setVisible(false);
      tileContainer.add(brightLabel);

      // Sweep a strip from the left edge of the label to past the right.
      // `value` is the RIGHT edge of the strip, advancing each frame.
      // Crop is clamped to the label's actual bounds and the bright
      // copy is hidden when no part of the strip overlaps the text, so
      // there's no "stuck at first letter" frame at rest.
      const labelStripW = Math.max(px(10), Math.round(label.width * 0.22));
      const labelStartDelay = (tile.id.length * 521) % 1400;
      const labelW = label.width;
      const labelH = brightLabel.height;
      this.tweens.addCounter({
        from: 0,
        to: labelW + labelStripW,
        duration: 900,
        ease: 'Sine.InOut',
        repeat: -1,
        repeatDelay: 900,
        delay: labelStartDelay,
        onUpdate: (tw) => {
          const right = Math.round(tw.getValue() ?? 0);
          const cropX = Math.max(0, right - labelStripW);
          const cropEnd = Math.min(labelW, right);
          const cropW = cropEnd - cropX;
          if (cropW <= 0) {
            brightLabel.setVisible(false);
          } else {
            brightLabel.setVisible(true);
            brightLabel.setCrop(cropX, 0, cropW, labelH);
          }
        },
      });
    }
  }

  private drawInfoIcon(
    parent: Phaser.GameObjects.Container,
    tile: LobbyTableTile,
    relCx: number,
    relCy: number,
    absCx: number,
    absCy: number,
  ) {
    const px = (n: number) => Math.round(n * this.dpr);
    const iconR = px(11);

    const g = this.add.graphics();
    g.fillStyle(0xd4af37, 1);
    g.fillCircle(relCx, relCy, iconR);
    g.lineStyle(px(1.5), 0x1a0e08, 0.55);
    g.strokeCircle(relCx, relCy, iconR);
    parent.add(g);

    const t = this.add.text(relCx, relCy, 'i', {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: `${px(13)}px`,
      color: '#1a0e08',
      fontStyle: 'bold italic',
    }).setOrigin(0.5);
    parent.add(t);

    // Hit area is generous (radius ~18 buffer-px) for finger taps, even
    // though the visible icon is smaller. Stored in absolute contentRoot
    // coords so handleTap / hover paths can match without traversing the
    // per-tile container.
    this.infoIconHits.push({ tileId: tile.id, x: absCx, y: absCy, r: px(18) });
  }

  private handleTap(x: number, contentY: number) {
    // Info-icon hits take priority over tile-body hits — the icon sits
    // INSIDE the tile rect, so without this short-circuit a tap on the
    // icon would also count as a tap on the tile and start the table
    // session.
    for (const hit of this.infoIconHits) {
      const dx = x - hit.x;
      const dy = contentY - hit.y;
      if (dx * dx + dy * dy <= hit.r * hit.r) {
        track('lobby_info_pill_opened', { table_id: hit.tileId });
        this.openInfoModal(hit.tileId);
        return;
      }
    }
    for (const room of this.layout.rooms) {
      for (const tile of room.tiles) {
        const inBounds =
          x >= tile.x && x <= tile.x + tile.w &&
          contentY >= tile.y && contentY <= tile.y + tile.h;
        if (!inBounds) continue;
        // Tap wiggle — fires before navigation so the player sees the
        // tile acknowledge the press. Brief enough (160 ms total) that
        // the subsequent 120 ms nav delay still feels instant.
        this.wiggleTile(tile.id);
        if (tile.unlocked) {
          this.time.delayedCall(120, () => this.events.emit('table:tap', tile.id));
        } else if (tile.lockReason) {
          const reason = tile.lockReason;
          this.time.delayedCall(120, () => this.showLockReasonToast(reason));
        }
        return;
      }
    }
  }

  private wiggleTile(tileId: string): void {
    const container = this.tileContainers.get(tileId);
    if (!container) return;
    this.tweens.add({
      targets: container,
      angle: { from: -2, to: 2 },
      duration: 80,
      yoyo: true,
      repeat: 1,
    });
  }

  private openInfoModal(tileId: string) {
    if (this.infoModalNodes) this.closeInfoModal();
    this.setHover(null);
    const W = this.scale.width;
    const H = this.scale.height;
    const px = (n: number) => Math.round(n * this.dpr);

    // Look up the live rules from persistence so the modal reads what the
    // table currently accepts (rather than a hardcoded copy of the tier
    // defaults). If lookup fails, bail rather than render an empty card.
    const persisted = load();
    const [gameId, tableKey] = tileId.split(':') as ['craps', string];
    const table = persisted.games[gameId]?.tables[tableKey];
    if (!table) return;

    const tile = this.findTile(tileId);
    const rows = buildRulesRows(table.rules as Record<string, unknown>);

    // Modal panel sized to its content. Centered horizontally; vertically
    // anchored slightly above mid-screen so the gold "ⓘ" still reads as
    // "this is about that tile up there" rather than floating in space.
    const lineH = px(22);
    const headerH = px(70);
    const footerH = px(20);
    const panelW = Math.min(W - px(40), px(420));
    const panelH = headerH + lineH * rows.length + footerH;
    const panelX = Math.round((W - panelW) / 2);
    const panelY = Math.round((H - panelH) / 2);

    // Backdrop graphics only — NOT interactive. The scene-level
    // pointerdown handler owns dismiss-on-tap-outside (gated by
    // infoModalPanelRect) so the same gesture also blocks the
    // table-selection propagation onto the tile beneath the modal.
    const dim = this.add.graphics().setDepth(OVERLAY_DEPTH);
    dim.fillStyle(0x000000, 0.65);
    dim.fillRect(0, 0, W, H);

    const bg = this.add.graphics().setDepth(OVERLAY_CARD_DEPTH);
    bg.fillStyle(0x0d2c1d, 1);
    bg.fillRoundedRect(panelX, panelY, panelW, panelH, px(12));
    bg.lineStyle(px(2), 0xd4af37, 1);
    bg.strokeRoundedRect(panelX, panelY, panelW, panelH, px(12));

    const title = this.add.text(
      panelX + panelW / 2,
      panelY + px(22),
      tile?.label ?? tileId,
      {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${px(18)}px`,
        color: '#f4d35e',
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0).setDepth(OVERLAY_CARD_DEPTH);

    const subtitle = this.add.text(
      panelX + panelW / 2,
      panelY + px(46),
      'CRAPS',
      {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${px(11)}px`,
        color: '#8a99a3',
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0).setDepth(OVERLAY_CARD_DEPTH);

    this.infoModalNodes = [dim, bg, title, subtitle];

    let y = panelY + headerH;
    for (const { label, value } of rows) {
      const lText = this.add.text(panelX + px(20), y, label, {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${px(13)}px`,
        color: '#aebfc7',
      }).setDepth(OVERLAY_CARD_DEPTH);
      const vText = this.add.text(panelX + panelW - px(20), y, value, {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${px(13)}px`,
        color: '#f4d35e',
        fontStyle: 'bold',
      }).setOrigin(1, 0).setDepth(OVERLAY_CARD_DEPTH);
      this.infoModalNodes.push(lText, vText);
      y += lineH;
    }
  }

  private closeInfoModal() {
    if (!this.infoModalNodes) return;
    for (const n of this.infoModalNodes) n.destroy();
    this.infoModalNodes = undefined;
    if (this.hoverCapable) {
      const p = this.input.activePointer;
      this.updateHoverFromPointer(p.x, p.y);
    }
  }

  private findTile(tileId: string): LobbyTableTile | undefined {
    for (const room of this.layout.rooms) {
      for (const tile of room.tiles) {
        if (tile.id === tileId) return tile;
      }
    }
    return undefined;
  }

  private playUnlockAnimations(): void {
    for (const room of this.layout.rooms) {
      if (!this.roomsAwaitingCelebration.has(room.id)) continue;
      this.playRopeDrop(room);
      // Flip the flag for every tile in the room and persist.
      for (const tile of room.tiles) {
        save(markUnlockSeenInLobby(load(), tile.id));
      }
    }
  }

  private playRopeDrop(room: LobbyRoom): void {
    // The rope was drawn inside drawRoom() — but we don't have a handle to it
    // from here without additional plumbing. Rather than retrofitting handles,
    // we draw a NEW rope on top in the same visual position, animate it, and
    // destroy it. The drawRoom-rendered rope underneath stays as the resting
    // decoration after the animation completes.
    const W = this.scale.width;
    const px = (n: number) => Math.round(n * this.dpr);
    // Rope anchor follows the room's new rangebar pill — the celebration
    // animation hangs from the divider area so the drop reads as "the
    // velvet rope holding back this room just fell." dropRope is a
    // scene-root object (not in contentRoot), so we add vMargin to match
    // the divider's actual world y when scrolled to the top.
    const ropeBaseY = room.dividerY + px(10) + this.vMargin;

    const dropRope = this.add.graphics().setDepth(20);
    const drawRope = (rotationRad: number, alpha: number) => {
      dropRope.clear();
      // Draw rope as a single 6px-thick line that pivots from the LEFT post.
      // Phaser graphics aren't tween-friendly for rotation, so we re-draw
      // explicit endpoints each frame.
      const leftX = px(20);
      const rightX = W - px(20);
      const length = rightX - leftX;
      const rightDx = Math.cos(rotationRad) * length;
      const rightDy = Math.sin(rotationRad) * length;
      dropRope.lineStyle(px(6), room.id === 'salon-prive' ? 0xd4af37 : 0x6b1f24, alpha);
      dropRope.beginPath();
      dropRope.moveTo(leftX, ropeBaseY);
      dropRope.lineTo(leftX + rightDx, ropeBaseY + rightDy);
      dropRope.strokePath();
      // Gold ball on the right end (still attached, ish).
      dropRope.fillStyle(0xc9a23a, alpha);
      dropRope.fillCircle(leftX + rightDx, ropeBaseY + rightDy, px(6));
    };
    const proxy = { angle: 0, alpha: 1 };
    drawRope(proxy.angle, proxy.alpha);

    // 0–200ms: hold (gives the eye time to register the rope)
    // 200–500ms: swing down to ~90° via gravity ease
    // 500–700ms: fade to 0
    this.time.delayedCall(200, () => {
      this.tweens.add({
        targets: proxy,
        angle: Math.PI / 2,
        duration: 300,
        ease: 'Cubic.In',
        onUpdate: () => drawRope(proxy.angle, proxy.alpha),
        onComplete: () => {
          this.tweens.add({
            targets: proxy,
            alpha: 0,
            duration: 200,
            onUpdate: () => drawRope(proxy.angle, proxy.alpha),
            onComplete: () => dropRope.destroy(),
          });
        },
      });
    });
  }

  private showLockReasonToast(reason: string): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const px = (n: number) => Math.round(n * this.dpr);
    const margin = px(20);
    const toastH = px(48);
    const toastW = Math.min(W - margin * 2, px(360));
    const toastX = W / 2 - toastW / 2;
    const startY = H + px(10);
    const targetY = H - margin - toastH;

    const bg = this.add.graphics().setDepth(950);
    const draw = (y: number) => {
      bg.clear();
      bg.fillStyle(0x1a0e08, 0.95);
      bg.fillRoundedRect(toastX, y, toastW, toastH, px(8));
      bg.lineStyle(px(1), 0xd4af37, 0.7);
      bg.strokeRoundedRect(toastX, y, toastW, toastH, px(8));
    };
    draw(startY);

    const text = this.add.text(W / 2, startY + toastH / 2, reason, {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${px(13)}px`,
      color: '#d4af37',
    }).setOrigin(0.5).setDepth(951);

    const proxy = { y: startY };
    this.tweens.add({
      targets: proxy,
      y: targetY,
      duration: 200,
      ease: 'Cubic.Out',
      onUpdate: () => { draw(proxy.y); text.setY(proxy.y + toastH / 2); },
      onComplete: () => {
        this.time.delayedCall(1800, () => {
          this.tweens.add({
            targets: proxy,
            y: startY,
            duration: 200,
            ease: 'Cubic.In',
            onUpdate: () => { draw(proxy.y); text.setY(proxy.y + toastH / 2); },
            onComplete: () => { bg.destroy(); text.destroy(); },
          });
        });
      },
    });
  }
}

interface RuleRow { label: string; value: string }

function fmtMoney(cents: unknown): string {
  if (typeof cents !== 'number') return '—';
  return `$${Math.floor(cents / 100).toLocaleString('en-US')}`;
}

function buildRulesRows(rules: Record<string, unknown>): RuleRow[] {
  const ats = rules.atsPayouts as { small?: number; tall?: number; all?: number } | undefined;
  return [
    { label: 'Min bet',     value: fmtMoney(rules.tableMin) },
    { label: 'Max bet',     value: fmtMoney(rules.tableMax) },
    { label: 'Odds cap',    value: String(rules.oddsCap ?? '3-4-5×').replace('x', '×') },
    { label: 'Small / Tall', value: `${ats?.small ?? 30}× / ${ats?.tall ?? 30}×` },
    { label: 'All',         value: `${ats?.all ?? 150}×` },
  ];
}
